import { Link } from "react-router-dom";
import { t } from "ttag";

import { ROUTES } from "@/app/router";
import { useActiveSubscriptions } from "@/features/billing/hooks";
import { useAvailableRefillRequests } from "@/features/my-meds/hooks";
import { PRICE_TYPE } from "@/shared/domains";
import { Button } from "@/shared/ui";
import { RequestCard } from ".";
import { NewMedicationRequestButton } from "../new-medication-request-button";

const getTexts = (
  hasAvailableRefillRequests: boolean,
  hasOnlyOneTimeProducts: boolean,
) => {
  if (hasAvailableRefillRequests) {
    return {
      title: t`Do you need medication?`,
      description: t`You don't have an open request.`,
    };
  }

  if (hasOnlyOneTimeProducts) {
    return {
      title: t`Want to repeat?`,
      description: t`Your product was a one-time purchase.`,
    };
  }

  return {
    description: t`Refills currently unavailable.`,
  };
};

export const EmptyMedicationRequestCard = () => {
  const { data: availableRefillRequests } = useAvailableRefillRequests();
  const { data: activeSubscriptions } = useActiveSubscriptions();

  const hasAvailableRefillRequests = Boolean(availableRefillRequests?.length);
  const hasOnlyOneTimeProducts = Boolean(
    activeSubscriptions?.every(
      (subscription) =>
        subscription.currentProduct.price.type === PRICE_TYPE.ONE_TIME,
    ),
  );

  const hasEnabledRefillRequest = Boolean(
    availableRefillRequests?.some(
      ({ isRefillable, hasUpcomingAppointment }) =>
        isRefillable && !hasUpcomingAppointment,
    ),
  );

  const showGoToShopButton =
    !hasAvailableRefillRequests && hasOnlyOneTimeProducts;

  const { title, description } = getTexts(
    hasAvailableRefillRequests,
    hasOnlyOneTimeProducts,
  );

  return (
    <RequestCard.Root className="gap-4 pt-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex flex-col gap-1">
        {title && (
          <RequestCard.Title className="font-semibold">
            {title}
          </RequestCard.Title>
        )}

        <RequestCard.Description>{description}</RequestCard.Description>
      </div>

      <RequestCard.Footer className="justify-center">
        {showGoToShopButton ? (
          <Button size="lg" className="w-full" asChild>
            <Link to={ROUTES.SHOP.OVERVIEW}>{t`Go to Shop`}</Link>
          </Button>
        ) : (
          <NewMedicationRequestButton disabled={!hasEnabledRefillRequest} />
        )}
      </RequestCard.Footer>
    </RequestCard.Root>
  );
};
